import React, { useState, useEffect } from 'react';
import './GalleryPage.css';

const GalleryPage = () => {
  const [artworks, setArtworks] = useState([]);
  const [filteredArtworks, setFilteredArtworks] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedArtist, setSelectedArtist] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const artworksPerPage = 16;

  // Sample data - في التطبيق الحقيقي سيأتي من API
  useEffect(() => {
    const sampleArtworks = Array.from({ length: 200 }, (_, index) => ({
      id: index + 1,
      title: `عمل فني رقم ${index + 1}`,
      artist: `الفنان ${Math.floor(Math.random() * 30) + 1}`,
      category: ['لوحات زيتية', 'رسومات', 'فن رقمي', 'أكريليك', 'مائية', 'فحم'][Math.floor(Math.random() * 6)],
      description: `وصف للعمل الفني رقم ${index + 1} الذي يعبر عن رؤية فنية مميزة`,
      createDate: new Date(2024, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1),
      dimensions: `${Math.floor(Math.random() * 50) + 20} × ${Math.floor(Math.random() * 50) + 20} سم`,
      medium: ['زيت على قماش', 'أكريليك', 'مائية', 'فحم', 'رقمي'][Math.floor(Math.random() * 5)],
      price: Math.random() > 0.7 ? `${Math.floor(Math.random() * 2000) + 500} جنيه` : 'غير متاح للبيع',
      likes: Math.floor(Math.random() * 100),
      views: Math.floor(Math.random() * 500) + 50,
      tags: ['طبيعة', 'بورتريه', 'تجريدي', 'معاصر', 'كلاسيكي'].slice(0, Math.floor(Math.random() * 3) + 1)
    }));
    setArtworks(sampleArtworks);
    setFilteredArtworks(sampleArtworks);
  }, []);

  // Filter and search logic
  useEffect(() => {
    let filtered = artworks;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(artwork => 
        artwork.title.includes(searchTerm) || 
        artwork.artist.includes(searchTerm) ||
        artwork.description.includes(searchTerm) ||
        artwork.tags.some(tag => tag.includes(searchTerm))
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(artwork => artwork.category === selectedCategory);
    }

    // Artist filter
    if (selectedArtist !== 'all') {
      filtered = filtered.filter(artwork => artwork.artist === selectedArtist);
    }

    // Sort
    filtered = filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.createDate) - new Date(a.createDate);
        case 'oldest':
          return new Date(a.createDate) - new Date(b.createDate);
        case 'title':
          return a.title.localeCompare(b.title);
        case 'artist':
          return a.artist.localeCompare(b.artist);
        case 'popular':
          return b.likes - a.likes;
        case 'views':
          return b.views - a.views;
        default:
          return 0;
      }
    });

    setFilteredArtworks(filtered);
    setCurrentPage(1);
  }, [artworks, searchTerm, selectedCategory, selectedArtist, sortBy]);

  // Pagination
  const indexOfLastArtwork = currentPage * artworksPerPage;
  const indexOfFirstArtwork = indexOfLastArtwork - artworksPerPage;
  const currentArtworks = filteredArtworks.slice(indexOfFirstArtwork, indexOfLastArtwork);
  const totalPages = Math.ceil(filteredArtworks.length / artworksPerPage);

  const categories = ['all', 'لوحات زيتية', 'رسومات', 'فن رقمي', 'أكريليك', 'مائية', 'فحم'];
  const artists = ['all', ...new Set(artworks.map(artwork => artwork.artist))].slice(0, 21);

  const openLightbox = (artwork) => {
    setSelectedArtwork(artwork);
  };

  const closeLightbox = () => {
    setSelectedArtwork(null);
  };

  return (
    <div className="gallery-page">
      <div className="page-header">
        <div className="container">
          <h1>معرض الأعمال الفنية</h1>
          <p>استمتع بمشاهدة أجمل الأعمال الفنية من فنانينا المبدعين</p>
        </div>
      </div>

      <div className="gallery-content">
        <div className="container">
          {/* Filters and Search */}
          <div className="gallery-controls">
            <div className="search-section">
              <div className="search-box">
                <i className="fas fa-search"></i>
                <input
                  type="text"
                  placeholder="ابحث عن عمل فني أو فنان..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div className="filter-section">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="filter-select"
              >
                <option value="all">جميع الفئات</option>
                {categories.slice(1).map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>

              <select
                value={selectedArtist}
                onChange={(e) => setSelectedArtist(e.target.value)}
                className="filter-select"
              >
                <option value="all">جميع الفنانين</option>
                {artists.slice(1).map(artist => (
                  <option key={artist} value={artist}>{artist}</option>
                ))}
              </select>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="filter-select"
              >
                <option value="newest">الأحدث</option>
                <option value="oldest">الأقدم</option>
                <option value="title">حسب العنوان</option>
                <option value="artist">حسب الفنان</option>
                <option value="popular">الأكثر إعجاباً</option>
                <option value="views">الأكثر مشاهدة</option>
              </select>
            </div>

            <div className="view-controls">
              <button
                className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
              >
                <i className="fas fa-th"></i>
              </button>
              <button
                className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
              >
                <i className="fas fa-list"></i>
              </button>
            </div>
          </div>

          {/* Results Info */}
          <div className="results-info">
            <p>عرض {indexOfFirstArtwork + 1}-{Math.min(indexOfLastArtwork, filteredArtworks.length)} من {filteredArtworks.length} عمل فني</p>
          </div>

          {/* Gallery Grid/List */}
          <div className={`gallery-${viewMode}`}>
            {currentArtworks.map((artwork) => (
              <div key={artwork.id} className={`artwork-card ${viewMode}`} onClick={() => openLightbox(artwork)}>
                <div className="artwork-image">
                  <i className="fas fa-palette"></i>
                  <div className="artwork-overlay">
                    <div className="artwork-stats">
                      <span><i className="fas fa-heart"></i> {artwork.likes}</span>
                      <span><i className="fas fa-eye"></i> {artwork.views}</span>
                    </div>
                    <button className="view-btn-overlay">
                      <i className="fas fa-expand"></i>
                    </button>
                  </div>
                </div>
                <div className="artwork-info">
                  <span className="artwork-category">{artwork.category}</span>
                  <h3 className="artwork-title">{artwork.title}</h3>
                  <p className="artwork-artist">الفنان: {artwork.artist}</p>
                  {viewMode === 'list' && (
                    <>
                      <p className="artwork-description">{artwork.description}</p>
                      <div className="artwork-meta">
                        <span>الأبعاد: {artwork.dimensions}</span>
                        <span>الوسط: {artwork.medium}</span>
                        <span>التاريخ: {artwork.createDate.toLocaleDateString('ar-EG')}</span>
                      </div>
                    </>
                  )}
                  <div className="artwork-tags">
                    {artwork.tags.map((tag, index) => (
                      <span key={index} className="tag">{tag}</span>
                    ))}
                  </div>
                  {artwork.price !== 'غير متاح للبيع' && (
                    <div className="artwork-price">{artwork.price}</div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                <i className="fas fa-chevron-right"></i>
              </button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, index) => {
                let pageNumber;
                if (totalPages <= 5) {
                  pageNumber = index + 1;
                } else if (currentPage <= 3) {
                  pageNumber = index + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNumber = totalPages - 4 + index;
                } else {
                  pageNumber = currentPage - 2 + index;
                }

                return (
                  <button
                    key={pageNumber}
                    onClick={() => setCurrentPage(pageNumber)}
                    className={`pagination-btn ${currentPage === pageNumber ? 'active' : ''}`}
                  >
                    {pageNumber}
                  </button>
                );
              })}

              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="pagination-btn"
              >
                <i className="fas fa-chevron-left"></i>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {selectedArtwork && (
        <div className="lightbox" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>
              <i className="fas fa-times"></i>
            </button>
            <div className="lightbox-image">
              <i className="fas fa-palette"></i>
            </div>
            <div className="lightbox-info">
              <h2>{selectedArtwork.title}</h2>
              <p className="lightbox-artist">الفنان: {selectedArtwork.artist}</p>
              <p className="lightbox-description">{selectedArtwork.description}</p>
              <div className="lightbox-details">
                <div className="detail-item">
                  <strong>الفئة:</strong> {selectedArtwork.category}
                </div>
                <div className="detail-item">
                  <strong>الأبعاد:</strong> {selectedArtwork.dimensions}
                </div>
                <div className="detail-item">
                  <strong>الوسط:</strong> {selectedArtwork.medium}
                </div>
                <div className="detail-item">
                  <strong>تاريخ الإنشاء:</strong> {selectedArtwork.createDate.toLocaleDateString('ar-EG')}
                </div>
                {selectedArtwork.price !== 'غير متاح للبيع' && (
                  <div className="detail-item">
                    <strong>السعر:</strong> {selectedArtwork.price}
                  </div>
                )}
              </div>
              <div className="lightbox-tags">
                {selectedArtwork.tags.map((tag, index) => (
                  <span key={index} className="tag">{tag}</span>
                ))}
              </div>
              <div className="lightbox-actions">
                <button className="btn btn-primary">
                  <i className="fas fa-heart"></i>
                  إعجاب ({selectedArtwork.likes})
                </button>
                <button className="btn btn-secondary">
                  <i className="fas fa-share"></i>
                  مشاركة
                </button>
                {selectedArtwork.price !== 'غير متاح للبيع' && (
                  <button className="btn btn-primary">
                    <i className="fas fa-shopping-cart"></i>
                    شراء
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
